
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { apiCall } from '@/lib/api/api-helpers'
import type { JobStatus, JobType } from '@genieartinc/schemas'

/**
 * Job Store - Tracks AI generation jobs for the active canvas
 *
 * Responsibilities:
 * - Store active/recent job records
 * - Provide actions for updating job status (from WebSocket events)
 * - Load persisted jobs on canvas mount
 */

export interface JobRecord {
  id: string
  /** Browser request identity survives acknowledgement; no prompt or credentials are stored. */
  requestId?: string
  submission?: 'submitting' | 'unconfirmed' | 'rejected'
  type: JobType
  status: JobStatus
  nodeId?: string
  canvasId: string
  progress?: number
  message?: string
  output?: unknown
  error?: string
  executionTime?: number
  createdAt: string
  updatedAt?: string
}

interface PlaceholderMapping {
  nodeIds: string[]
  parentNodeId: string
}

interface JobStore {
  // State
  jobs: Map<string, JobRecord>
  activeCanvasId: string | null
  placeholderMap: Map<string, PlaceholderMapping>

  // Actions
  setCanvasId: (canvasId: string) => void
  addJob: (job: JobRecord) => void
  acknowledgeSubmission: (requestId: string, job: JobRecord) => void
  updateJobStatus: (jobId: string, status: JobStatus, data?: Partial<JobRecord>) => void
  updateJobProgress: (jobId: string, progress: number, message?: string) => void
  removeJob: (jobId: string) => void
  loadCanvasJobs: (canvasId: string) => Promise<void>
  clearJobs: () => void

  // Placeholder mapping (for async generation)
  setPlaceholders: (jobId: string, parentNodeId: string, nodeIds: string[]) => void
  getPlaceholders: (jobId: string) => PlaceholderMapping | undefined
  removePlaceholders: (jobId: string) => void

  // Selectors
  getActiveJobs: () => JobRecord[]
  getJobsByNode: (nodeId: string) => JobRecord[]
  getJobsByStatus: (status: JobStatus) => JobRecord[]
}

const SUBMISSION_CACHE = 'genie.job-submissions'
function recoverSubmissions(): Map<string, JobRecord> {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SUBMISSION_CACHE) || '[]') as JobRecord[]
    if (!Array.isArray(saved)) return new Map()
    return new Map(saved.filter(job => typeof job.id === 'string' && job.id.startsWith('request-') && typeof job.canvasId === 'string' && typeof job.createdAt === 'string').slice(-50).map(job => [job.id, {
      ...job, status: 'FAILED', submission: job.submission === 'rejected' ? 'rejected' : 'unconfirmed',
      message: job.submission === 'rejected' ? 'Submission rejected' : 'Submission not confirmed',
      error: job.error || 'The page closed before acknowledgement. Check Activity for accepted work before trying again.',
    }]))
  } catch { return new Map() }
}

export const useJobStore = create<JobStore>()(
  subscribeWithSelector((set, get) => ({
    jobs: recoverSubmissions(),
    activeCanvasId: null,
    placeholderMap: new Map(),

    setCanvasId: (canvasId: string) => {
      set({ activeCanvasId: canvasId })
    },

    addJob: (job: JobRecord) => {
      set((state) => {
        const next = new Map(state.jobs)
        const existing = next.get(job.id)
        // Late queue acknowledgement/replay must not resurrect completed work.
        const terminal = existing && ['COMPLETED', 'FAILED', 'CANCELED'].includes(existing.status)
        next.set(job.id, terminal && ['PENDING', 'RUNNING'].includes(job.status) ? { ...job, ...existing } : { ...existing, ...job })
        return { jobs: next }
      })
    },

    acknowledgeSubmission: (requestId, job) => {
      set((state) => {
        const next = new Map(state.jobs)
        const streamed = next.get(job.id)
        next.delete(requestId)
        // WebSocket completion can arrive before the POST response.
        next.set(job.id, { ...job, ...streamed, requestId, submission: undefined })
        return { jobs: next }
      })
    },

    updateJobStatus: (jobId: string, status: JobStatus, data?: Partial<JobRecord>) => {
      set((state) => {
        const existing = state.jobs.get(jobId)
        if (!existing) return state
        if (['COMPLETED', 'FAILED', 'CANCELED'].includes(existing.status) && ['PENDING', 'RUNNING'].includes(status)) return state

        const next = new Map(state.jobs)
        next.set(jobId, {
          ...existing,
          ...data,
          status,
          updatedAt: new Date().toISOString(),
        })
        return { jobs: next }
      })
    },

    updateJobProgress: (jobId: string, progress: number, message?: string) => {
      set((state) => {
        const existing = state.jobs.get(jobId)
        if (!existing || ['COMPLETED', 'FAILED', 'CANCELED'].includes(existing.status)) return state

        const next = new Map(state.jobs)
        next.set(jobId, {
          ...existing,
          progress,
          ...(message !== undefined ? { message } : {}),
          updatedAt: new Date().toISOString(),
        })
        return { jobs: next }
      })
    },

    removeJob: (jobId: string) => {
      set((state) => {
        const next = new Map(state.jobs)
        next.delete(jobId)
        return { jobs: next }
      })
    },

    loadCanvasJobs: async (canvasId: string) => {
      try {
        const result = await apiCall(
          `/api/jobs/canvas/${canvasId}`,
          true
        ) as { success: boolean; data: { jobs: Array<Record<string, unknown>> } }

        if (result.success && result.data?.jobs) {
          set((state) => {
            const next = new Map(state.jobs)
            for (const raw of result.data.jobs) {
              const job: JobRecord = {
                id: raw.id as string,
                type: raw.type as JobType,
                status: raw.status as JobStatus,
                nodeId: raw.nodeId as string | undefined,
                // The API authorizes the UUID then returns its internal database canvas ID.
                canvasId,
                requestId: raw.input && typeof raw.input === 'object' && typeof (raw.input as Record<string, unknown>).requestId === 'string' ? (raw.input as Record<string, unknown>).requestId as string : undefined,
                progress: raw.progress as number | undefined,
                message: raw.message as string | undefined,
                output: raw.output,
                error: raw.error as string | undefined,
                executionTime: raw.executionTime as number | undefined,
                createdAt: (raw.createdAt as string) || new Date().toISOString(),
                updatedAt: raw.updatedAt as string | undefined,
              }
              if (job.requestId) next.delete(job.requestId)
              const existing = next.get(job.id)
              const terminal = existing && ['COMPLETED', 'FAILED', 'CANCELED'].includes(existing.status)
              if (!terminal || !['PENDING', 'RUNNING'].includes(job.status)) next.set(job.id, { ...existing, ...job })
            }
            return { jobs: next }
          })
        }
      } catch (err) {
        // Jobs endpoint may not exist yet during development - silent fail
        console.warn('Failed to load canvas jobs:', err)
      }
    },

    clearJobs: () => {
      set({ jobs: new Map(), placeholderMap: new Map() })
    },

    setPlaceholders: (jobId: string, parentNodeId: string, nodeIds: string[]) => {
      set((state) => {
        const next = new Map(state.placeholderMap)
        next.set(jobId, { nodeIds, parentNodeId })
        return { placeholderMap: next }
      })
    },

    getPlaceholders: (jobId: string) => {
      return get().placeholderMap.get(jobId)
    },

    removePlaceholders: (jobId: string) => {
      set((state) => {
        const next = new Map(state.placeholderMap)
        next.delete(jobId)
        return { placeholderMap: next }
      })
    },

    getActiveJobs: () => {
      const { jobs } = get()
      return Array.from(jobs.values()).filter(
        (j) => j.status === 'PENDING' || j.status === 'RUNNING'
      )
    },

    getJobsByNode: (nodeId: string) => {
      const { jobs } = get()
      return Array.from(jobs.values()).filter((j) => j.nodeId === nodeId)
    },

    getJobsByStatus: (status: JobStatus) => {
      const { jobs } = get()
      return Array.from(jobs.values()).filter((j) => j.status === status)
    },
  }))
)

export const getJobStore = () => useJobStore.getState()

// Retain only request receipts, never prompts, outputs, provider keys or input images.
useJobStore.subscribe((state) => {
  try {
    const receipts = [...state.jobs.values()].filter(job => job.submission).slice(-50).map(({ id, requestId, type, status, nodeId, canvasId, createdAt, submission, message, error }) => ({ id, requestId, type, status, nodeId, canvasId, createdAt, submission, message, error }))
    sessionStorage.setItem(SUBMISSION_CACHE, JSON.stringify(receipts))
  } catch { /* Storage is optional; the in-memory receipt remains visible. */ }
})
